import { useCallback } from 'react';
import { Link as RouterLink, generatePath } from 'quartz-routing/react-router';
import { useTicketDetailsParams } from '../hooks/useTicketDetailsParams';
import { Flex } from 'foundations-components/components/Flex';
import styled from 'foundations-theming-specialty/utils';
import { ROUTES } from '../constants/routes';
import { useHasConfiguredColumn } from '../utils/useHasConfiguredColumn';
import { useConfiguredColumnLabelOverride } from '../utils/useConfiguredColumnLabelOverride';
import I18n from 'I18n';
import { useOnDelayedHover } from '../utils/useOnDelayedHover';
import { usePrefetchTicketData } from '../hooks/usePrefetchTicketData';
import { usageTracker } from '../utils/usageTracker';
import { InboxNavTicketRowHeader } from './InboxNavTicketRowHeader';
import { InboxNavTicketRowContent } from './InboxNavTicketRowContent';
import { formatTicketId } from '../utils/formatTicketId';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RowLink = styled(RouterLink).withConfig({
  displayName: "InboxNavTicketRow__RowLink"
})(["display:block;padding-block:", ";padding-inline:", ";border-block-end:", " solid ", ";text-decoration:none;color:inherit;&:hover{background-color:", ";text-decoration:none;}&[data-selected='true'],&[data-selected='true']:hover{background-color:", ";}"], ({
  theme
}) => theme.space['300'], ({
  theme
}) => theme.space['600'], ({
  theme
}) => theme.borderWidth[100], ({
  theme
}) => theme.color['border-core-subtle'], ({
  theme
}) => theme.color['fill-surface-recessed'], ({
  theme
}) => theme.color['fill-surface-recessed']);
export const InboxNavTicketRow = ({
  ticket,
  onClick
}) => {
  var _ticket$createDate, _ticket$requestedBy;
  const {
    ticketId: currentTicketId
  } = useTicketDetailsParams();
  const prefetchTicketData = usePrefetchTicketData();
  const isStatusConfigured = useHasConfiguredColumn('status');
  const isRequestedByConfigured = useHasConfiguredColumn('__requested_by_full_name_or_email');
  const requestedByDisplayName = useConfiguredColumnLabelOverride('__requested_by_full_name_or_email');
  const showOpenDot = isStatusConfigured && !ticket.isClosed;
  const showClosedDot = isStatusConfigured && !!ticket.isClosed;
  const ticketId = ticket.ticketId;
  const isSelected = currentTicketId === ticketId;
  const title = ticket.subject || I18n.text('customer-portal-app.inboxNavPanel.ticketRow.fallbackTitle', {
    ticketId: formatTicketId(ticketId)
  });
  const timestamp = (_ticket$createDate = ticket.createDate) !== null && _ticket$createDate !== void 0 ? _ticket$createDate : null;
  const content = ticket.content && ticket.content.length > 0 ? ticket.content : null;
  const requestedBy = isRequestedByConfigured ? (_ticket$requestedBy = ticket.requestedBy) !== null && _ticket$requestedBy !== void 0 ? _ticket$requestedBy : null : undefined;
  const handleClick = useCallback(() => {
    usageTracker.track('Customer Portal App', 'Tickets Discovered', {
      action: 'ticket-expanded',
      screen: 'ticket-details',
      initiator: 'inbox-nav-row-click'
    });
    onClick === null || onClick === void 0 || onClick();
  }, [onClick]);
  const onHover = useCallback(id => prefetchTicketData(Number(id)), [prefetchTicketData]);
  const hoverProps = useOnDelayedHover(onHover);
  return /*#__PURE__*/_jsx("div", {
    role: "listitem",
    children: /*#__PURE__*/_jsx(RowLink, Object.assign({}, hoverProps, {
      id: String(ticketId),
      to: generatePath(ROUTES.ticket, {
        ticketId
      }),
      onClick: handleClick,
      "data-test-id": "inbox-nav-ticket-row",
      "data-selected": isSelected,
      "aria-label": title,
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        gap: "100",
        children: [/*#__PURE__*/_jsx(InboxNavTicketRowHeader, {
          title: title,
          timestamp: timestamp,
          showOpenDot: showOpenDot,
          showClosedDot: showClosedDot
        }), /*#__PURE__*/_jsx(InboxNavTicketRowContent, {
          content: content,
          requestedBy: requestedBy,
          requestedByDisplayName: requestedByDisplayName
        })]
      })
    }))
  });
};